'use client';

/**
 * useAssistantConnection — the connection machine's driver hook.
 *
 * Replaces the legacy useWebSocketConnection.ts. Owns the real WebSocket and
 * all timers, but NO policy: every lifecycle/outbox decision is delegated to
 * the pure reducer (reducer.ts) and this hook merely executes the effects it
 * returns. Inbound server-event dispatch (handleServerEvent) is RELOCATED
 * VERBATIM from the legacy hook — do not "improve" those handler bodies here;
 * the deferred-card ordering logic in particular is a live-verified P0 fix.
 *
 * INVARIANT: connection LIFECYCLE (open/close/error/backoff/session-change)
 * never writes UIState — only user intent and semantic server events
 * (HISTORY_RESTORED, HANDOFF_STARTED, HANDOFF_ENDED/HANDOFF_TIMEOUT) do. The
 * session-change effect picks LANDING vs CHAT off the store's
 * `expectingHistory` INTENT flag, never off socket state.
 *
 * Wiring: ChatContext exposes `connectionDispatchRef`; this hook registers
 * its dispatch there so ChatContext's sendText/retryMessage/startNewChat can
 * feed user intent into the machine (context-as-bus, like wsRef).
 *
 * Stale-closure safety: socket callbacks live longer than a render, so every
 * mutable thing they touch flows through refs (`ctxRef`, `machineRef`, timer
 * refs). Handler closures may be from an old render; the refs are not.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ConnectionStatus,
  ErrorBucket,
  MessageRole,
  RealtimeErrorCode,
  WSEventType,
  UIState,
  VizMode,
  TurnPhase,
  type AgentAttachment,
  type ContactField,
  ToolName,
  type HandoffDomain,
  type HandoffEndReason,
  type Message,
  type TurnState,
  type ToolRetryOutcomeState,
} from '@/types/chat';
import {
  callStartIndexFromRestore,
  restoredMessagesToMessages,
  type RestoredRow,
} from '@/lib/restore-messages';
import { toast } from 'sonner';
import { useConversationStore } from '@/stores/conversation-store';
import { getDeviceToken } from '@/lib/device-token';
import { getAssistantWsUrl } from '@/lib/assistant-config';
import { looksLikeLeakedToolArgs } from '@/lib/message-guards';
import { unwrapMcpOutput } from '@/lib/mcp-envelope';
import { searchedLocationFromArgs } from '@/lib/course-grouping';
import { routeToolResultToPin } from '@/lib/pin-routing';
import { resolveHistoryItemReconcile } from '@/lib/history-reconcile';
import { insertIndexFromSnapshot } from '@/lib/history-order';
import { handleRtcWsEvent, rejectAllPending } from '@/lib/rtc-ws-negotiate';
import { useChatContext } from '@/contexts/ChatContext';
import { connectionReducer, phaseToConnectionStatus } from './reducer';
import { shouldAcceptTurnState } from './turn-state';
import {
  shouldDeferContactForm,
  shouldIgnoreLegacyHandoffTrigger,
} from './handoff-guard';
import {
  INITIAL_STATE,
  OutboundFrameKind,
  type ConnEffect,
  type ConnEvent,
  type ConnState,
} from './types';
import {
  nextPageContextAction,
  INITIAL_PAGE_CONTEXT_STATE,
  PAGE_CONTEXT_ACK_TIMEOUT_MS,
  type PageContextAckStatus,
  type PageContextEvent,
  type PageContextMachineState,
} from './page-context';

/** Flatten a history item's content parts into displayable text — the four
 *  part shapes the SDK uses across text and voice (text / input_text carry
 *  .text; audio / input_audio carry .transcript). Shared by the
 *  HISTORY_UPDATED reconcile, its pre-updater flush gate, and HISTORY_ADDED
 *  so the three can never drift on what counts as "prose". */
function extractItemText(item: any): string {
  let content = '';
  if (Array.isArray(item?.content)) {
    item.content.forEach((part: any) => {
      if (part?.type === 'text' && part.text) content += part.text;
      else if (part?.type === 'input_text' && part.text) content += part.text;
      else if (part?.type === 'audio' && part.transcript)
        content += part.transcript;
      else if (part?.type === 'input_audio' && part.transcript)
        content += part.transcript;
    });
  }
  return content.trim();
}

/** How long the resume skeleton may wait for a history resolution event
 *  (history_restored / conversation_reset / history_replay_failed) after the
 *  settings frame is sent, before falling back to the empty chat. Generous —
 *  it only ever fires against a backend that stays silent for a
 *  no-rows-yet conversation. */
const HISTORY_RESTORE_TIMEOUT_MS = 8000;

export function useAssistantConnection() {
  const ctx = useChatContext();
  const sessionId = useConversationStore((s) => s.sessionId);
  const queryClient = useQueryClient();

  // Latest context, readable from long-lived socket callbacks.
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  // ── Machine state + driver-owned timers ───────────────────────
  // The machine lives in a ref: transport transitions must not re-render the
  // tree. UI-visible projections (ConnectionStatus, Message.status) are
  // pushed out explicitly by dispatch/effects.
  const machineRef = useRef<ConnState>(INITIAL_STATE);
  const retryTimerRef = useRef<number | null>(null);
  const ackTimerRef = useRef<number | null>(null);
  // Page-context handshake (machines/connection/page-context.ts) — same
  // "real machine lives in a ref, store is a projection" split as
  // machineRef/ConnectionStatus above. This ref is the ONLY thing that may
  // retain `rejected` across a socket close (page-load-scoped, not
  // socket-scoped — see page-context.ts's file header); the store's mirror
  // always resets to idle on close (WS-E.3's literal contract).
  const pageContextMachineRef = useRef<PageContextMachineState>(
    INITIAL_PAGE_CONTEXT_STATE
  );
  const pageContextAckTimerRef = useRef<number | null>(null);
  // True after unmount cleanup — silences socket callbacks and blocks timers.
  const disposedRef = useRef(false);
  // Session-recovery (reconnect reset): true once THIS hook instance has
  // opened a socket at least once. openSocket() runs on every OPEN_SOCKET
  // effect — the initial connect AND every later reconnect (recovery
  // retry, or SESSION_CHANGED's immediate reopen) — so `ws.onopen` alone
  // can't tell them apart. A fresh mount always starts false, correctly
  // treating that mount's first-ever open as "not a reconnect" even
  // though the underlying sessionId may be reused across mounts.
  const hasConnectedOnceRef = useRef(false);

  // ── Text-mode tool-card deferral ──────────────────────────────
  // In TEXT mode a tool card must render AFTER the assistant's text reply (to
  // match the seq-ordered restored view). The card's tool result is ready BEFORE
  // the prose (the tool runs, then the model writes its multi-second reply), so we
  // stash cards at tool-time and flush them the moment the prose lands — i.e. from
  // the SAME handler that inserts the assistant text (history_updated /
  // history_added). Because React applies functional setState updates in queue
  // order, "append text, then append card" deterministically yields text→card with
  // no dependency on event/clock timing. AGENT_END flushes too, as a backstop for
  // tool-only turns (no prose). The timer below is a last-resort safety only —
  // anchoring to a fixed delay was the original bug (it fired mid-generation, so
  // the card landed above the still-pending prose). VOICE mode inserts immediately
  // (continuous audio; no text to order against). Keyed by content so the two live
  // card paths (tool_end + the history_added function_call item) collapse to one.
  const deferredCardsRef = useRef<
    Map<
      string,
      {
        toolName: string;
        payload: unknown;
        id: string;
        searchedLocation?: string;
      }
    >
  >(new Map());
  // The pre-handoff contact form (Decision #17) rides the SAME deferral: its
  // contact_info_requested event fires at tool time, so rendering it
  // immediately puts the form ABOVE Sara's "please fill this in" prose — the
  // exact misorder the card stash fixes. One slot (not a map): at most one
  // pending form exists per session, and a re-emit for the same handoff just
  // overwrites it. Flushed AFTER the tool cards (the interactive element
  // closes the turn). See shouldDeferContactForm for the two immediate-insert
  // bypasses (voice mode / reconnect re-send).
  const deferredContactFormRef = useRef<{
    handoffId: string;
    fields: ContactField[];
  } | null>(null);
  const deferredFlushTimerRef = useRef<number | null>(null);

  // Content-keys of RAG cards already inserted THIS turn. RAG cards compose with
  // the answer (hero→answer→sources) at render time, so — unlike course cards —
  // the "card below prose" ordering the deferral protects doesn't apply; they
  // insert immediately (so the hero shows while the answer streams). This set
  // dedups the two live paths (tool_end + the history_added function_call item)
  // for the same result. Cleared at turn end / session change / socket close.
  const insertedCardKeysRef = useRef<Set<string>>(new Set());

  // ── turn_state ordering guard ─────────────────────────────────
  // Newest turn_id seen this connection; -1 = none. See turn-state.ts for
  // the stale-idle race this guards against. Reset on socket close (turn
  // ids restart per backend session).
  const lastTurnIdRef = useRef(-1);
  // True while the backend reports a turn in flight (latest accepted
  // turn_state phase ≠ idle). A ref, not ctx.turnState: turn_state and
  // contact_info_requested can arrive in the same tick, and React state
  // wouldn't have committed yet (same reason isCallingRef exists). Feeds
  // shouldDeferContactForm; reset with lastTurnIdRef on socket close.
  const turnActiveRef = useRef(false);

  // ── Live-agent handoff: legacy dual-emit guard ─────────────────
  // True once HANDOFF_STARTED has been handled this connection — the paired
  // legacy HANDOFF_TRIGGER (Decision #15, dual-emit) is then pure noise (see
  // handoff-guard.ts). Reset on socket close: a fresh backend session may
  // legitimately re-emit a handoff pair the client hasn't seen yet.
  const sawHandoffStartedRef = useRef(false);

  // ── Effect executor ───────────────────────────────────────────

  function executeEffect(effect: ConnEffect) {
    const c = ctxRef.current;
    switch (effect.type) {
      case 'OPEN_SOCKET': {
        if (disposedRef.current) return;
        // An explicit open supersedes any pending recovery retry.
        if (retryTimerRef.current != null) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = null;
        }
        openSocket();
        break;
      }
      case 'CLOSE_SOCKET': {
        const ws = c.wsRef.current;
        if (
          ws &&
          (ws.readyState === WebSocket.OPEN ||
            ws.readyState === WebSocket.CONNECTING)
        ) {
          ws.close(1000, effect.reason);
        }
        break;
      }
      case 'SEND_FRAMES': {
        const ws = c.wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // The reducer only emits SEND_FRAMES for an OPEN phase; if the
          // socket died in the same tick, the imminent SOCKET_CLOSED will
          // re-queue these (ack-capable) — nothing to do here.
          return;
        }
        for (const item of effect.items) {
          // Same outbox/ack/retry lifecycle, different wire shape: a
          // handoff reply becomes an agent_text frame (carrying handoff_id)
          // instead of Sara's plain text frame. See send-routing.ts for the
          // pure decision that sets item.frameKind.
          const payload =
            item.frameKind === OutboundFrameKind.AGENT_TEXT
              ? {
                  type: WSEventType.AGENT_TEXT,
                  handoff_id: item.handoffId,
                  text: item.text,
                  client_msg_id: item.id,
                }
              : {
                  type: 'text',
                  text: item.text,
                  // client_msg_id: ack correlation + backend at-most-once dedup.
                  // Preserves the legacy payload shape (backend reads only `text`).
                  metadata: { client_generated: true, client_msg_id: item.id },
                };
          ws.send(JSON.stringify(payload));
        }
        break;
      }
      case 'SCHEDULE_RETRY': {
        if (disposedRef.current) return;
        if (retryTimerRef.current != null) clearTimeout(retryTimerRef.current);
        retryTimerRef.current = window.setTimeout(() => {
          retryTimerRef.current = null;
          dispatch({ type: 'RETRY_TICK' });
        }, effect.delayMs);
        break;
      }
      case 'SCHEDULE_ACK_TIMEOUT': {
        if (disposedRef.current) return;
        // One timer per burst; a newer burst extends the deadline for the
        // older one, which is harmless — ACK_TIMEOUT is a no-op when nothing
        // is un-acked.
        if (ackTimerRef.current != null) clearTimeout(ackTimerRef.current);
        ackTimerRef.current = window.setTimeout(() => {
          ackTimerRef.current = null;
          dispatch({ type: 'ACK_TIMEOUT' });
        }, effect.delayMs);
        break;
      }
      case 'NOTIFY_TERMINAL': {
        // Retry budget exhausted — stop the loop and tell the user.
        // Badge stays 'disconnected'; a new send/retry (or reload) revives.
        // This is also the ONE place a connection problem ends a live call
        // (a plain drop rides through reconnects — see ws.onclose): with no
        // more retries coming there is nothing for the call to wait for.
        //
        // [FB34] `stopCallIfActive()` (phase-aware), not `isCallingRef` —
        // isCallingRef is still false throughout `connecting`, so a socket
        // dying mid-connect used to leave the half-built call running
        // silently instead of tearing it down. Returns whether a call was
        // actually stopped, so the two toast copies still key on that.
        if (c.stopCallIfActive()) {
          toast.error(
            'Lost connection to Sara — the call ended. Check your network and call again.'
          );
        } else {
          toast.error(
            'Lost connection to Sara. Check your network and reload the page to reconnect.'
          );
        }
        break;
      }
      case 'UPDATE_MESSAGE_STATUS': {
        const byId = new Map(effect.updates.map((u) => [u.id, u.status]));
        c.setMessages((prev) =>
          prev.map((m) => {
            const status = byId.get(m.id);
            return status && m.status !== status ? { ...m, status } : m;
          })
        );
        break;
      }
    }
  }

  function dispatch(event: ConnEvent) {
    const prevPhase = machineRef.current.phase;
    const { state, effects } = connectionReducer(machineRef.current, event);
    machineRef.current = state;
    if (state.phase !== prevPhase) {
      // Badge status is a projection of machine phase. The server-driven
      // upstream RECONNECTING/RECONNECTED events overlay this (display-only —
      // they describe the backend↔OpenAI hop, not our socket; see D6).
      ctxRef.current.setConnectionStatus(phaseToConnectionStatus(state.phase));
    }
    for (const effect of effects) executeEffect(effect);
  }

  // ── Page-context handshake driver ──────────────────────────────
  // Drives machines/connection/page-context.ts's pure decision function —
  // see that file's header for the full handshake. Wired from THREE call
  // sites below: ws.onopen (every socket open), a store subscription (a
  // payload arriving from the host while already open — embed-content.tsx
  // sets it, this hook reacts), and handleServerEvent's PAGE_CONTEXT_ACK
  // case. ws.onclose feeds SOCKET_CLOSED.

  function clearPageContextAckTimer() {
    if (pageContextAckTimerRef.current != null) {
      clearTimeout(pageContextAckTimerRef.current);
      pageContextAckTimerRef.current = null;
    }
  }

  function armPageContextAckTimer() {
    if (disposedRef.current) return;
    // One timer per in-flight frame; a fresh SEND_HEAD/SEND_BODY always
    // supersedes whatever deadline was running (same "one timer per burst"
    // idiom as SCHEDULE_ACK_TIMEOUT above).
    clearPageContextAckTimer();
    pageContextAckTimerRef.current = window.setTimeout(() => {
      pageContextAckTimerRef.current = null;
      dispatchPageContext({ type: 'ACK_TIMEOUT' });
    }, PAGE_CONTEXT_ACK_TIMEOUT_MS);
  }

  /** Build and send one `page_context` wire frame. `sections` is omitted
   *  entirely for a head frame (undefined), or the `missing`-filtered map
   *  for a body frame — see PageContextMessage's docstring
   *  (aol_schemas.ws_events) for why the manifest rides on BOTH frames. */
  function sendPageContextFrame(
    requestId: string,
    sections?: Record<string, string>
  ) {
    const payload = useConversationStore.getState().pageContext.payload;
    if (!payload) {
      // Can only happen if the store's payload was cleared between the
      // decision function being invoked and this executing — both run
      // synchronously in the same dispatch, so this is defense-in-depth,
      // not a path any test needs to exercise.
      console.warn(
        '[page-context] no payload held — skipping the frame the machine just asked for'
      );
      return;
    }
    const ws = ctxRef.current.wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // The machine only ever asks for a send while it believes the socket
      // is open (SOCKET_OPENED/PAYLOAD_ARRIVED carry that as an explicit
      // fact) — a mismatch here means the socket died in the same tick;
      // the imminent SOCKET_CLOSED will reset the handshake.
      return;
    }
    ws.send(
      JSON.stringify({
        type: WSEventType.PAGE_CONTEXT,
        request_id: requestId,
        schema_version: 1,
        url: payload.url,
        title: payload.title,
        lang: payload.lang,
        signals: payload.signals,
        outline: payload.outline,
        sections,
        truncated: payload.truncated,
      })
    );
    armPageContextAckTimer();
  }

  function dispatchPageContext(event: PageContextEvent) {
    const prev = pageContextMachineRef.current;
    const { state, action } = nextPageContextAction(prev, event);
    pageContextMachineRef.current = state;

    // The in-flight request this event resolved (if any) no longer needs
    // its deadline — SEND_BODY below re-arms a fresh one when the ack asks
    // for a body frame.
    if (
      event.type === 'SOCKET_CLOSED' ||
      (event.type === 'ACK_RECEIVED' && event.requestId === prev.requestId)
    ) {
      clearPageContextAckTimer();
    }

    if (event.type === 'SOCKET_CLOSED') {
      // Store projection ALWAYS resets to idle on close (WS-E.3) — the ref
      // above is the one source of truth that may keep `rejected` alive
      // across the close; see this function's header comment.
      useConversationStore.getState().resetPageContextPhase();
    } else {
      useConversationStore.getState().setPageContextMachineState(state);
    }

    switch (action.type) {
      case 'SEND_HEAD':
        sendPageContextFrame(action.requestId, undefined);
        break;
      case 'SEND_BODY': {
        const payload = useConversationStore.getState().pageContext.payload;
        const sections: Record<string, string> = {};
        if (payload) {
          for (const id of action.missing) {
            const text = payload.sections[id];
            if (text !== undefined) sections[id] = text;
          }
        }
        sendPageContextFrame(action.requestId, sections);
        break;
      }
      case 'WARN':
        console.warn(`[page-context] ${action.message}`);
        break;
      case 'NONE':
        break;
    }
  }

  // ── Socket construction ───────────────────────────────────────

  function openSocket() {
    const c = ctxRef.current;
    const sid = c.sessionIdRef.current;
    const primaryUrl = getAssistantWsUrl(sid);

    let ws: WebSocket;
    try {
      ws = new WebSocket(primaryUrl);
    } catch (err) {
      console.error('[WebSocket] constructor error', err);
      c.setConnectionStatus('disconnected');
      // A constructor throw (malformed URL, mixed-content/CSP block) will
      // not self-heal via retry, so surface it once instead of leaving the
      // assistant silently dead with only a console line.
      toast.error('Unable to reach Sara. Please reload the page to try again.');
      return;
    }

    // Raw handle stays on the context: call-mode audio frames bypass the
    // outbox by design and send directly on this ref (useCallMode).
    c.wsRef.current = ws;

    ws.onopen = () => {
      if (disposedRef.current) return;
      // Always send the durable conversation_id FIRST — the backend requires
      // settings as the literal first frame and does device-scoped
      // get-or-create: resume if it exists for this device, else start a new
      // conversation under this id (so a reload with the same ?c= resumes).
      const storeState = useConversationStore.getState();
      const msg: Record<string, unknown> = {
        type: WSEventType.SETTINGS,
        device_token: getDeviceToken(),
        conversation_id: storeState.conversationId,
      };
      ws.send(JSON.stringify(msg));
      // Page-context handshake — after settings, before SOCKET_OPENED (plan
      // §WS-E.4). Re-sent on EVERY open (D3: control frames are not
      // replayed after a drop) when the panel is holding a payload from the
      // host; a no-payload embed (or the non-embedded route, where nothing
      // ever calls setPageContextPayload) is a silent no-op.
      dispatchPageContext({
        type: 'SOCKET_OPENED',
        hasPayload: storeState.pageContext.payload != null,
        requestId: crypto.randomUUID(),
      });
      // SOCKET_OPENED drains any queued outbox items strictly AFTER settings.
      dispatch({ type: 'SOCKET_OPENED' });
      // A call that rode out a drop (see onclose) is back on the air —
      // retract the "Connection lost" notice. No-op outside a call or when
      // no drop was surfaced.
      ctxRef.current.onCallConnectionRestored();
      // Session-recovery (reconnect reset): every open AFTER the first one
      // this hook instance has ever seen is a genuine reconnect — the
      // backend's pending_tool_retry bookkeeping is wiped on disconnect and
      // never repopulated (a per-CONNECTION guarantee, not per-incident;
      // see ChatContext's onSocketReconnected doc comment), so a live
      // tool-timeout offer surviving the blip can no longer trust its
      // retry_id. No-op if nothing is listening (no live offer).
      if (hasConnectedOnceRef.current) {
        ctxRef.current.onSocketReconnected();
      } else {
        hasConnectedOnceRef.current = true;
      }

      // Safety net for the resume skeleton: the backend resolves every
      // client-supplied conversation_id with exactly one of history_restored
      // (possibly empty) / conversation_reset / history_replay_failed — but an
      // OLDER backend stays silent for a conversation with no rows yet. If no
      // resolution lands in time, drop the skeleton and show the empty chat
      // rather than hanging on it forever.
      if (storeState.expectingHistory) {
        window.setTimeout(() => {
          if (useConversationStore.getState().expectingHistory) {
            console.warn(
              '[WebSocket] history restore timed out — showing empty chat'
            );
            useConversationStore.getState().setExpectingHistory(false);
          }
        }, HISTORY_RESTORE_TIMEOUT_MS);
      }
    };

    ws.onmessage = (event) => {
      // Same superseded-socket guards as onclose/onerror: a session switch
      // (sidebar click → SESSION_CHANGED) constructs the replacement socket
      // immediately, but the OLD socket can still drain frames already in
      // flight. Observed live: a deferred conversation's empty
      // history_restored landing AFTER a switch — it wiped the new
      // conversation's restore skeleton (setMessages([]) +
      // expectingHistory=false) and flashed the starter grid until the real
      // restore arrived. Only the CURRENT socket may feed state.
      if (disposedRef.current) return;
      if (ctxRef.current.wsRef.current !== ws) return;
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error('Error parsing WebSocket message', err);
        return;
      }
      // Errors in handleServerEvent propagate with proper stack traces
      // instead of being swallowed as "parse errors"
      handleServerEvent(data);
    };

    ws.onerror = () => {
      // Presentational only (legacy parity): the close event that follows
      // drives the actual recovery transition through the machine.
      // Same superseded-socket guards as onclose: an aborted PREVIOUS socket
      // (StrictMode dev remount, reconnect swap) fires its error event async,
      // AFTER the replacement socket is already connecting — without the
      // guard it stamped the badge "Disconnected" for the whole handshake.
      if (disposedRef.current) return;
      if (ctxRef.current.wsRef.current !== ws) return;
      ctxRef.current.setConnectionStatus('disconnected');
      // [SDP-over-WS] This socket is dying (or already dead) — flush any
      // rtc_call negotiations it was carrying. onclose (below) does the
      // same; rejectAllPending is idempotent (empty registry ⇒ no-op) so
      // calling it from both handlers for the same drop is harmless. See
      // rtc-ws-negotiate.ts's own doc comment for why NONE of these pending
      // entries will ever see their ack/answer/failed frame now.
      rejectAllPending();
    };

    ws.onclose = (event) => {
      console.warn(`[WebSocket] closed: ${event.code} ${event.reason}`);
      if (disposedRef.current) return;
      // A superseded socket's straggling close must not feed the machine —
      // only the CURRENT socket's lifecycle counts.
      if (ctxRef.current.wsRef.current !== ws) return;

      // [SDP-over-WS] See the identical call/comment in onerror above —
      // this socket owned any pending rtc_call negotiations, and none of
      // them will ever see a server frame now.
      rejectAllPending();

      // A live call SURVIVES the drop (FB28): the capture chain doesn't
      // depend on the socket — its pump drops chunks while closed and
      // resumes into the reconnected session automatically (the backend
      // re-arms voice mode off the first audio frame). Ending the call here
      // was also unrecoverable-by-code on iOS (rebuilding the audio chain
      // needs a fresh user gesture). The call surface just says why Sara
      // went quiet; retry exhaustion (NOTIFY_TERMINAL) is what ends it.
      ctxRef.current.onCallConnectionDrop();
      // [machine] The backend session died with the socket — any in-flight
      // turn is gone. Settle any half-streamed caret, drop the per-turn card
      // dedup, clear the turn signal (null = fall back to the legacy
      // message-shape derivation) and reset the ordering guard: turn ids
      // restart with the next backend session.
      ctxRef.current.finalizeStreaming();
      insertedCardKeysRef.current.clear();
      ctxRef.current.setTurnState(null);
      lastTurnIdRef.current = -1;
      turnActiveRef.current = false;
      sawHandoffStartedRef.current = false;
      // A drop landing AFTER TOOL_START but BEFORE its paired TOOL_END never
      // gets that pairing to clear the indicator — TOOL_END is the only other
      // place that resets it, and it's never coming for this turn. Without
      // this, the orb is stuck showing THINKING/searching until the NEXT
      // turn happens to start one. Same reset TOOL_END / ERROR / RECONNECTING
      // / AUDIO_INTERRUPTED all perform below (cleanupResources in
      // ChatContext.tsx does the isThinkingRef+ticker half at unmount, but
      // never runs on a mid-session socket close — this mirrors it here).
      if (ctxRef.current.isThinkingRef.current) {
        ctxRef.current.isThinkingRef.current = false;
        ctxRef.current.stopThinkingTicker();
        ctxRef.current.resetToIdle();
      }
      // A form stashed mid-turn must not wait out the fallback timer once its
      // turn died with the socket — flush it now so reconnect's re-send path
      // (which inserts immediately) never races a stale deferred copy.
      flushDeferredCards();
      dispatchPageContext({ type: 'SOCKET_CLOSED' });
      dispatch({ type: 'SOCKET_CLOSED' });
    };
  }

  // ── Main lifecycle: mount/unmount ─────────────────────────────
  useEffect(() => {
    // Fresh machine per mount (StrictMode-safe: the dev double-mount gets a
    // clean IDLE machine instead of a stuck 'connecting' from the first pass).
    disposedRef.current = false;
    machineRef.current = INITIAL_STATE;
    // Same StrictMode-safe reset as machineRef above — this ref (not the
    // store) is the actual page-context machine; see its declaration.
    pageContextMachineRef.current = INITIAL_PAGE_CONTEXT_STATE;
    useConversationStore.getState().resetPageContextPhase();
    // Register the machine's inbox on the context so ChatContext actions
    // (sendText / retryMessage / startNewChat) can feed user intent in.
    ctxRef.current.connectionDispatchRef.current = dispatch;
    dispatch({ type: 'CONNECT_REQUESTED' });

    // A page-context payload can arrive from the host WHILE this hook's
    // socket is already open (embed-content.tsx's PAGE_CONTEXT handler
    // calls setPageContextPayload — a same-panel navigation, or the initial
    // extract landing after `ready` on a slow host page). ws.onopen alone
    // only covers the "payload already held at open time" half of D3;
    // this subscription covers the other half ("arrives later while open").
    // No-ops on the non-embedded route: nothing there ever sets a payload,
    // so `payload` stays null and every trigger below is filtered out.
    const unsubscribePageContext = useConversationStore.subscribe(
      (state, prevState) => {
        if (state.pageContext.payload === prevState.pageContext.payload) {
          return;
        }
        if (state.pageContext.payload == null) return;
        const ws = ctxRef.current.wsRef.current;
        dispatchPageContext({
          type: 'PAYLOAD_ARRIVED',
          socketOpen: !!ws && ws.readyState === WebSocket.OPEN,
          requestId: crypto.randomUUID(),
        });
      }
    );

    return () => {
      disposedRef.current = true;
      const c = ctxRef.current;
      c.connectionDispatchRef.current = null;
      c.cleanupResources();
      unsubscribePageContext();

      if (retryTimerRef.current != null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (ackTimerRef.current != null) {
        clearTimeout(ackTimerRef.current);
        ackTimerRef.current = null;
      }
      clearPageContextAckTimer();
      if (deferredFlushTimerRef.current != null) {
        clearTimeout(deferredFlushTimerRef.current);
        deferredFlushTimerRef.current = null;
      }

      if (c.wsRef.current) {
        if (
          c.wsRef.current.readyState === WebSocket.OPEN ||
          c.wsRef.current.readyState === WebSocket.CONNECTING
        ) {
          try {
            c.wsRef.current.send(JSON.stringify({ type: 'client_disconnect' }));
          } catch {
            // Ignore send errors during cleanup
          }
          c.wsRef.current.close(1000, 'Client disconnected normally');
        }
        c.wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Session ID change → intentional reconnect ─────────────────
  // Fired by user intent only (startNewConversation / resumeConversation mint
  // a new sessionId). The machine reconnects IMMEDIATELY (no recovery backoff)
  // and the screen reflects the INTENT: resume expects a history replay →
  // CHAT + skeleton; a brand-new chat has nothing to load → LANDING.
  useEffect(() => {
    if (sessionId === ctx.sessionIdRef.current) return;
    ctx.sessionIdRef.current = sessionId;

    // Drop any stashed (un-flushed) tool cards / contact form from the previous
    // conversation so they can't leak into the new one.
    deferredCardsRef.current.clear();
    deferredContactFormRef.current = null;
    insertedCardKeysRef.current.clear();
    ctx.finalizeStreaming();
    if (deferredFlushTimerRef.current != null) {
      clearTimeout(deferredFlushTimerRef.current);
      deferredFlushTimerRef.current = null;
    }

    ctx.setMessages([]);
    // A stale handoff object must not leak into the next conversation, even
    // though uiState alone already prevents it from driving anything —
    // defense in depth (see useHandoff.clearHandoff).
    ctx.clearHandoff();
    // Same defense-in-depth for a stale tool-timeout offer — a card left
    // over from the previous conversation must not resurface in a new one.
    ctx.setToolTimeoutOffer(null);
    // A stale retry outcome is scoped to the OLD session's pending_tool_retry
    // entry (backend in-memory state, never persisted) — meaningless once
    // that session is gone.
    ctx.setToolRetryOutcome(null);
    const { expectingHistory, freshChatIntent } =
      useConversationStore.getState();
    // Three landings: resume → CHAT (skeleton until HISTORY_RESTORED);
    // explicit "New" click → CHAT too, but empty (page.tsx shows the starter
    // grid — design T6: the sidebar stays put, no bounce out to the marketing
    // LANDING screen); anything else (first visit's initial session) → LANDING.
    ctx.setUIState(
      expectingHistory || freshChatIntent ? UIState.CHAT : UIState.LANDING
    );
    if (freshChatIntent) {
      useConversationStore.setState({ freshChatIntent: false });
    }

    // The machine clears the outbox (a message typed against conversation A
    // must never flush into conversation B), closes the live socket, and
    // reopens immediately under the new sessionId.
    dispatch({ type: 'SESSION_CHANGED' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // ── Tool-card deferral helpers ────────────────────────────────

  // Last-resort safety: if BOTH the prose-landing flush AND the AGENT_END backstop
  // somehow never fire, flush stashed cards after this delay so a card is never
  // permanently lost. Deliberately long — short delays race the model's reply and
  // were the original "card above prose" bug. Normal turns flush on prose-land
  // (fast) or AGENT_END well before this fires; flushDeferredCards() clears it.
  const DEFERRED_CARD_FALLBACK_MS = 10000;

  const flushDeferredCards = () => {
    if (deferredFlushTimerRef.current != null) {
      clearTimeout(deferredFlushTimerRef.current);
      deferredFlushTimerRef.current = null;
    }
    const form = deferredContactFormRef.current;
    if (deferredCardsRef.current.size === 0 && form == null) return;
    const cards = Array.from(deferredCardsRef.current.values());
    deferredCardsRef.current.clear();
    deferredContactFormRef.current = null;
    for (const card of cards) {
      ctxRef.current.insertToolResult(
        card.toolName,
        card.payload,
        card.id,
        card.searchedLocation
      );
    }
    // Form last: it's the interactive element that closes the turn, so it
    // sits below any informational cards (and below the prose that queued
    // this flush).
    if (form != null) {
      ctxRef.current.contactInfoRequested(form.handoffId, form.fields);
    }
    setTimeout(
      () => ctxRef.current.chatLayoutRef.current?.scrollToBottom(300),
      10
    );
  };

  // Arm the last-resort flush timer if it isn't already running — shared by
  // every deferral site so a stashed item can never be permanently lost.
  const armDeferredFlushFallback = () => {
    if (deferredFlushTimerRef.current == null) {
      deferredFlushTimerRef.current = window.setTimeout(() => {
        deferredFlushTimerRef.current = null;
        flushDeferredCards();
      }, DEFERRED_CARD_FALLBACK_MS);
    }
  };

  const stashOrInsertToolCard = (
    toolName: string,
    payload: unknown,
    itemId?: string,
    /** find_courses: the searched zip/city derived from tool_end.arguments
     *  (searchedLocationFromArgs) — rides the stash so the deferred insert
     *  keeps it. */
    searchedLocation?: string
  ) => {
    // Voice mode renders the card immediately (no text reply to order against).
    if (ctxRef.current.isCallingRef.current) {
      ctxRef.current.insertToolResult(
        toolName,
        payload,
        itemId,
        searchedLocation
      );
      // Design turn 11 part B: also surface a pinned glance-card over the
      // call. Turn 14 reworked usePinnedCard from a single-slot replace into
      // a capped history (newest first, MAX_PINS) — pinCard pushes to the
      // front unconditionally rather than replacing, so an earlier pinned
      // video survives (peekable in the mobile stack, 14c) instead of being
      // silently dropped. Independent of insertToolResult above: that call
      // still lands this result in chat history for after the call ends.
      const pin = routeToolResultToPin(toolName, payload);
      if (pin) ctxRef.current.pinCard(pin);
      return;
    }
    // Key by CONTENT (not itemId) so the tool_end path (no itemId) and the
    // function_call path (itemId) for the same result collapse to one entry.
    const key = `${toolName}:${JSON.stringify(payload)?.slice(0, 256) ?? ''}`;

    // RAG cards compose with the answer at render time (hero→answer→sources), so
    // they insert IMMEDIATELY — the hero is present while the answer streams,
    // with no card-below-prose ordering to protect. Dedup across the two live
    // paths via insertedCardKeysRef (cleared at turn end).
    if (
      toolName === ToolName.BROWSE_AOL_CONTENT ||
      toolName === ToolName.FETCH_QUICK_AOL_CONTENT ||
      toolName === ToolName.SEARCH_AOL_CONTENT
    ) {
      if (insertedCardKeysRef.current.has(key)) return;
      insertedCardKeysRef.current.add(key);
      const id = itemId || `tool-${toolName}-${Date.now().toString(36)}`;
      ctxRef.current.insertToolResult(toolName, payload, id);
      setTimeout(
        () => ctxRef.current.chatLayoutRef.current?.scrollToBottom(300),
        10
      );
      return;
    }

    // Non-RAG (course) cards: defer until AGENT_END so they land BELOW the prose.
    // First stash wins the render id.
    if (!deferredCardsRef.current.has(key)) {
      const id = itemId || `tool-${toolName}-${Date.now().toString(36)}`;
      deferredCardsRef.current.set(key, {
        toolName,
        payload,
        id,
        searchedLocation,
      });
    }
    armDeferredFlushFallback();
  };

  // ── Server-event handler ──────────────────────────────────────
  // RELOCATED VERBATIM from useWebSocketConnection.ts (ctx → ctxRef.current is
  // the only mechanical change). Three sanctioned additions, each tagged
  // [machine]: the MESSAGE_RECEIVED case, and setExpectingHistory(false) in
  // HISTORY_RESTORED + the HISTORY_REPLAY_FAILED error branch.

  function handleServerEvent(event: Record<string, unknown>) {
    const ctx = ctxRef.current;
    const eventType = event.type as string;

    // Diagnostic: log non-audio events to trace tool_end delivery.
    // TEXT_DELTA is high-frequency (one per token) — omit it from the log.
    if (
      eventType !== WSEventType.AUDIO_DATA &&
      eventType !== WSEventType.TEXT_DELTA
    ) {
      console.log(`[WS] ${eventType}`, event);
    }

    switch (eventType) {
      case WSEventType.AGENT_START: {
        // A new assistant utterance is beginning. NOTE: this used to clear
        // the separate LiveUtterance model (FB22) as a belt-and-braces for
        // id-less streams — that model is gone. Voice streaming now upserts
        // by item_id through appendAssistantDelta (the same pipeline text
        // mode uses), and a new response always streams under a NEW
        // item_id, so there's no stale entry here for this event to retire.
        break;
      }

      case WSEventType.TURN_STATE: {
        // [machine] Backend-authoritative turn activity — drives the typing
        // dots + status line directly (no message-shape inference). Guarded
        // by turn_id so a late-firing idle from the previous turn can't
        // clear the fresh turn's indicator (see turn-state.ts).
        const phase = event.phase as TurnState['phase'];
        const tool = (event.tool as string | null) ?? null;
        const turnId = event.turn_id as number;
        if (typeof turnId !== 'number') break;
        if (!shouldAcceptTurnState(lastTurnIdRef.current, turnId)) break;
        lastTurnIdRef.current = turnId;
        // Synchronous mirror for shouldDeferContactForm — see the ref's
        // declaration comment.
        turnActiveRef.current = phase !== TurnPhase.IDLE;
        // idle stores null — one code path for "turn over" and "no signal",
        // which is exactly what the composite indicator rule wants.
        if (phase === TurnPhase.IDLE) {
          // Turn over — settle any lingering stream caret and reset the
          // per-turn RAG-card dedup so the next turn's cards insert fresh.
          ctx.finalizeStreaming();
          insertedCardKeysRef.current.clear();
          ctx.setTurnState(null);
        } else {
          ctx.setTurnState({ phase, tool });
          if (phase === TurnPhase.RESPONDING) {
            // [FB32] Assistant output has actually started — the cleanest
            // existing "Sara responded" signal, so retract any lingering
            // MODEL_BUSY notice (rate_limit_wait) here rather than wiring a
            // separate hook off TRANSCRIPT_DELTA/AUDIO_DATA. No-op when
            // nothing is showing on that topic (every normal turn reaches
            // RESPONDING, not just a rate-limited one).
            ctx.onModelResponded();
          }
        }
        break;
      }

      case WSEventType.HISTORY_UPDATED: {
        const history = event.history as unknown[];
        if (!Array.isArray(history)) return;

        // The ordered item_id list of the WHOLE snapshot (every item type,
        // not just messages — a function_call carries item_id too, e.g. a
        // tool card inserted via HISTORY_ADDED under that same id). This is
        // the adjacency source of truth for the user-voice-item insert
        // below: an item's true position in the conversation is wherever it
        // sits in THIS list, independent of which frame happened to deliver
        // it or when (see lib/history-order.ts).
        const snapshotIds = history
          .map((item) => (item as { item_id?: unknown })?.item_id)
          .filter(
            (id): id is string => typeof id === 'string' && id.length > 0
          );

        // Text→card flush gate, decided from the SNAPSHOT ITSELF before any
        // state call. The previous form set a closure flag INSIDE the
        // setMessages updater and read it right after — but React only
        // guarantees updaters run at render time (it often ran synchronously
        // anyway via the empty-queue eager-evaluation fast path, which is an
        // implementation detail, not a contract), so the read could see a
        // stale false and silently skip the flush until the AGENT_END
        // backstop. The gate's actual job is only "never flush a card before
        // its prose exists": if the snapshot carries assistant prose, that
        // prose is either already rendered or being inserted by the updater
        // queued just below — and flushDeferredCards' own inserts queue AFTER
        // it, so text→card ordering holds either way. (Deliberately WIDER
        // than the old newMessages-only flag: a reconcile-only snapshot also
        // flushes now, which is safe — the prose is already on screen.)
        const snapshotHasAssistantProse = history.some((item: any) => {
          if (item?.type !== 'message' || item.role !== 'assistant')
            return false;
          const text = extractItemText(item);
          return !!text && !looksLikeLeakedToolArgs(text);
        });

        ctx.setMessages((prev) => {
          const indexById = new Map<string, number>();
          prev.forEach((m, i) => {
            if (m.id) indexById.set(m.id, i);
          });
          const appendedIds = new Set<string>();
          const newMessages: Message[] = [];
          // Copy-on-first-replacement — stays null (and we return `prev`,
          // React bails out) when the snapshot brought nothing new. SHARED
          // by the assistant replace-in-place branch below AND the user
          // voice-item insert branch, so the two can never diverge into two
          // independent copies of `prev` within the same pass (a real risk:
          // a mid-response snapshot routinely carries BOTH an in-progress
          // assistant item and the user's just-completed voice transcript).
          // INVARIANT: because the array is shared, `indexById` must track
          // the WORKING COPY, not `prev` — every user splice below shifts
          // its map entries and registers the inserted id, so a later
          // assistant item in the same pass (the streaming reply typically
          // FOLLOWS the user item in snapshot order) still reads/writes its
          // own element and not whatever slid into its old index. Reading a
          // stale index here is not cosmetic: the authority rule would see
          // the user bubble (not streaming) instead of the streaming reply,
          // judge the snapshot authoritative, and overwrite the user's text
          // with Sara's partial transcript.
          let reconciled: Message[] | null = null;

          history.forEach((item: any) => {
            if (item.type === 'message' && item.role === 'assistant') {
              const id = item.item_id;
              if (id && appendedIds.has(id)) return;

              const content = extractItemText(item);
              if (!content || looksLikeLeakedToolArgs(content)) return;

              const existingIdx = id ? indexById.get(id) : undefined;
              if (existingIdx !== undefined) {
                // Reconcile-authority rule (resolveHistoryItemReconcile,
                // lib/history-reconcile.ts): a snapshot taken mid-response
                // carries a PARTIAL transcript, so while the delta stream
                // (appendAssistantDelta) still owns this message
                // (`streaming: true`) and the snapshot's own item is STILL
                // in progress, skip the replace outright — stomping it here
                // would visibly retract in-flight text and clear the caret
                // mid-word. Any other case (status 'completed'/'incomplete'
                // — barge-in truncation is final for that response —
                // missing status from an older/QA backend, or a
                // non-streaming existing message) makes the snapshot
                // authoritative: replace + clear `streaming`. Identical
                // content keeps the same element reference.
                const current = (reconciled ?? prev)[existingIdx];
                if (
                  resolveHistoryItemReconcile(
                    current.streaming,
                    item.status
                  ) === 'skip'
                ) {
                  return;
                }
                if (current.content !== content || current.streaming) {
                  if (!reconciled) reconciled = [...prev];
                  reconciled[existingIdx] = {
                    ...current,
                    content,
                    streaming: false,
                  };
                  // Buffer hygiene: this authoritative replace must drop the
                  // stream buffer for `id` — otherwise a stray late delta
                  // (appendAssistantDelta) would resurrect the OLD
                  // accumulation on top of the content just landed here (the
                  // same guard insertAssistantMessage applies on its own
                  // path — see useMessageManagement.ts).
                  if (current.streaming) ctx.dropStreamBuffer(id);
                }
                return;
              }
              newMessages.push({ role: 'assistant', content, id });
              if (id) appendedIds.add(id);
            } else if (item.type === 'message' && item.role === 'user') {
              // Voice-call transcript misorder fix: the side-channel
              // transcriber that produces the user's voice transcript can
              // finish AFTER the realtime model has already started
              // streaming its reply, so there is no "arrival order" that's
              // ever safe to append by. This snapshot is the one place the
              // full conversation order is known — position this item by
              // adjacency (insertIndexFromSnapshot) instead.
              const id = item.item_id;
              if (!id) return;
              // Dedup: already landed in `prev`, or already inserted earlier
              // in this same pass (splices register into `indexById`). A
              // completed transcript is stable — leave it alone rather than
              // updating in place (unlike the assistant branch, there's no
              // in-place content to reconcile).
              if (indexById.has(id)) return;

              // CRITICAL GUARD — voice items ONLY. Read content from
              // `input_audio` parts alone, never `input_text`. A text-mode
              // user turn is inserted locally (ChatContext.sendText) under a
              // LOCAL uuid at send time, so a snapshot's `input_text` user
              // item would never match that id — ingesting it here would
              // DUPLICATE every typed user bubble. Restricting extraction to
              // `input_audio` also means an in-progress transcription (no
              // transcript yet) naturally yields empty content and falls
              // through the skip below — one check does both jobs.
              let content = '';
              if (Array.isArray(item.content)) {
                item.content.forEach((part: any) => {
                  if (part?.type === 'input_audio' && part.transcript)
                    content += part.transcript;
                });
              }
              content = content.trim();
              if (!content) return;

              // Position against the CURRENT working copy.
              const insertAt = insertIndexFromSnapshot(
                reconciled ?? prev,
                snapshotIds,
                id
              );
              if (!reconciled) reconciled = [...prev];
              // [FB36 review] A voice transcription landing AFTER the call
              // ended (the sideband pump outlives the hangup by the detach
              // rebuild window) can never be answered — stamp it abandoned
              // at insertion so it doesn't re-latch the typing dots the
              // call-end settle just cleared. During a live call this is
              // the normal path and the stamp stays off.
              reconciled.splice(insertAt, 0, {
                role: 'user',
                content,
                id,
                ...(ctx.isCallingRef.current ? {} : { abandoned: true }),
              });
              // Keep `indexById` tracking the WORKING COPY (see its
              // invariant note above): everything at/after the splice point
              // shifted right by one, and the new id registers for dedup.
              indexById.forEach((idx, key) => {
                if (idx >= insertAt) indexById.set(key, idx + 1);
              });
              indexById.set(id, insertAt);
            }
          });

          // Same-reference bailout when the snapshot changed nothing —
          // avoids re-renders when HISTORY_UPDATED fires redundantly. Holds
          // for user inserts too: they only ever set `reconciled`, never
          // `newMessages`.
          if (newMessages.length === 0 && !reconciled) return prev;
          const base = reconciled ?? prev;
          return newMessages.length > 0 ? [...base, ...newMessages] : base;
        });

        // Prose landed via the snapshot path → flush stashed cards so their
        // append is queued AFTER the text appends (text→card). Gated on the
        // pre-computed snapshot scan above — user voice inserts alone never
        // flush; an empty snapshot never flushes a card before its prose
        // exists.
        if (snapshotHasAssistantProse) flushDeferredCards();

        // NOTE: this used to also derive a call-mode caption from the latest
        // assistant history entry. That's gone — TRANSCRIPT_DELTA now routes
        // straight into `messages` via appendAssistantDelta (the same
        // pipeline text mode uses), so a call's in-flight message already
        // lives here; the replace-in-place branch above is what keeps a
        // mid-response snapshot from stomping that live stream (see
        // resolveHistoryItemReconcile, lib/history-reconcile.ts).

        setTimeout(() => ctx.chatLayoutRef.current?.scrollToBottom(300), 10);
        break;
      }

      case WSEventType.HISTORY_ADDED: {
        const item = event.item as any;

        if (item?.type === 'message' && item?.role === 'assistant') {
          // NOTE: the old `metadata.tool_result` reader lived here and was dead —
          // the backend never sent that shape. Live tool cards come via TOOL_END
          // and the function_call branch below (both → insertToolResult); restored
          // cards come via HISTORY_RESTORED → the shared mapper.

          const content = extractItemText(item);
          if (content && !looksLikeLeakedToolArgs(content)) {
            ctx.insertAssistantMessage(item.item_id, content);
            // Prose just landed → flush any stashed tool cards now so their append
            // is queued right AFTER this text append (text→card). No-op if nothing
            // is stashed or in voice mode (cards insert immediately there).
            flushDeferredCards();
            // NOTE: no separate live-utterance model to reconcile here
            // (FB22 is gone). insertAssistantMessage already upserts by
            // item_id and drops that id's stream buffer itself (see
            // useMessageManagement.ts) — an in-call transcript landing via
            // HISTORY_ADDED reconciles exactly the way a text-mode message
            // does, with no call-mode-specific wiring needed.
          }
        } else if (
          item?.type === 'function_call' &&
          item?.status === 'completed'
        ) {
          // Delegate to insertToolResult — same unwrap + routing as tool_end
          const toolName = item.name as string | undefined;
          const outputStr = item.output as string | undefined;
          if (!toolName || !outputStr) break;

          try {
            const payload = unwrapMcpOutput(outputStr);
            stashOrInsertToolCard(toolName, payload, item.item_id);
            setTimeout(
              () => ctx.chatLayoutRef.current?.scrollToBottom(300),
              10
            );
          } catch (err) {
            console.warn('Failed to parse function_call output:', err);
          }
        }
        break;
      }

      case WSEventType.AUDIO_DATA: {
        const base64 = event.audio as string;
        if (
          ctx.isCallingRef.current &&
          typeof base64 === 'string' &&
          base64.length > 0
        ) {
          // NOTE: this used to also append `event.transcript` into a
          // separate live-caption model. That's gone — TRANSCRIPT_DELTA is
          // the sole source of streamed text (routed into `messages` via
          // appendAssistantDelta), so this handler only owns audio
          // playback.
          ctx.enqueueAudio(base64);
        }
        break;
      }

      case WSEventType.AUDIO_END:
        break;

      case WSEventType.TEXT_DELTA: {
        // Token-by-token assistant text. Correlate by the model item_id (matches
        // the id on the authoritative history_added item, so the final blob
        // reconciles the same bubble). Fall back to response_id if absent.
        const itemId =
          (event.item_id as string | undefined) ||
          (typeof event.response_id === 'string'
            ? `stream-${event.response_id}`
            : undefined);
        const delta = (event.delta as string) ?? '';
        const responseId =
          typeof event.response_id === 'string' ? event.response_id : undefined;
        if (itemId && delta)
          ctx.appendAssistantDelta(itemId, delta, responseId);
        break;
      }

      // Backend commentary/answer classification for a whole response_id
      // (response-scoping: response carried a function_call ⇒ commentary). Keeps
      // every message in the Thinking timeline until one is confirmed final,
      // then pushes it out to the answer.
      case WSEventType.MSG_CLASS: {
        const responseId = event.response_id as string | undefined;
        const isFinal = event.is_final as boolean | undefined;
        if (responseId && typeof isFinal === 'boolean')
          ctx.classifyMessages(responseId, isFinal);
        break;
      }

      case WSEventType.TRANSCRIPT_DELTA: {
        const delta = (event.delta as string) ?? '';
        if (delta) {
          const itemId =
            typeof event.item_id === 'string' && event.item_id
              ? (event.item_id as string)
              : null;

          if (!ctx.isCallingRef.current) {
            // Post-hang-up tail: the response keeps finishing server-side
            // after an early call end, so deltas keep arriving OUTSIDE the
            // call. INVARIANT: a delta may only ever extend the message it
            // belongs to (matched by item_id) — the old "append to whatever
            // is last" corrupted whichever message happened to sit last
            // (a tool card, a notice) once these frames became real. The id
            // here matches the message appendAssistantDelta already created
            // in `messages` DURING the call (see the in-call branch below —
            // voice streaming rides the same store-backed pipeline as text
            // now, so there's no separate hang-up commit to reconcile
            // against; the message is simply already there, still growing).
            // No match / no id → drop the delta; the authoritative final
            // text arrives via the history reconcile anyway.
            if (itemId) {
              ctx.setMessages((prev) => {
                const i = prev.findIndex((m) => m.id === itemId);
                if (i === -1) return prev;
                const copy = [...prev];
                copy[i] = {
                  ...copy[i],
                  content: (copy[i].content || '') + delta,
                };
                return copy;
              });
            }
          } else if (event.role === 'assistant' || !event.role) {
            // DURING a call: route straight into the message store via the
            // SAME appendAssistantDelta pipeline text mode uses (upsert by
            // item_id, streaming: true, StrictMode-safe stream buffers) —
            // the separate LiveUtterance model (FB22) is gone; voice and
            // text streaming are one pipeline now. The backend's
            // transcript_delta always carries item_id for a live SDK stream
            // (the Realtime SDK guarantees it) — an id-less delta has
            // nothing to upsert against, so it's dropped rather than
            // guessed at.
            const responseId =
              typeof event.response_id === 'string'
                ? event.response_id
                : undefined;
            if (itemId) ctx.appendAssistantDelta(itemId, delta, responseId);
          }
        }
        break;
      }

      case WSEventType.TOOL_START: {
        ctx.isThinkingRef.current = true;
        ctx.setVizMode(VizMode.THINKING);
        ctx.startThinkingTicker();
        break;
      }

      case WSEventType.TOOL_END: {
        if (ctx.isThinkingRef.current) {
          ctx.isThinkingRef.current = false;
          ctx.resetToIdle();
          ctx.stopThinkingTicker();
        }

        const toolName = event.tool as string | undefined;
        const toolOutput = event.output as string | undefined;
        console.log(
          '[tool_end] tool:',
          toolName,
          'output length:',
          toolOutput?.length
        );
        if (toolName && toolOutput) {
          const payload = unwrapMcpOutput(toolOutput);
          console.log(
            '[tool_end] unwrapped payload:',
            typeof payload,
            payload && typeof payload === 'object'
              ? Object.keys(payload)
              : payload
          );
          // find_courses: surface WHAT WAS SEARCHED (zip/city from the
          // forwarded tool-call arguments) so the panel header matches the
          // user's ask, not the results' own postal codes.
          const searchedLocation =
            toolName === ToolName.FIND_COURSES
              ? (searchedLocationFromArgs(
                  event.arguments as Record<string, unknown> | undefined
                ) ?? undefined)
              : undefined;
          stashOrInsertToolCard(toolName, payload, undefined, searchedLocation);
        }
        break;
      }

      case WSEventType.AGENT_END: {
        // End of the assistant's turn — the text reply has landed, so flush any
        // text-mode tool cards stashed during this turn (they render BELOW the
        // text, matching the seq-ordered restored view). No-op in voice mode /
        // when nothing was stashed.
        flushDeferredCards();
        break;
      }

      case WSEventType.HANDOFF_TRIGGER: {
        // [handoff migration] Legacy tool-action event — the backend
        // dual-emits this alongside HANDOFF_STARTED (Decision #15) as a
        // rollback safety net for an old cached frontend build that still
        // knows how to open the (now-deleted) HubSpot widget. This build's
        // entire handoff UI is driven by HANDOFF_STARTED below; the widget
        // auto-open flow this event used to drive was deleted along with the
        // widget bridge module it lived in. Ignore outright once
        // HANDOFF_STARTED has been handled this session — see handoff-guard.ts.
        if (shouldIgnoreLegacyHandoffTrigger(sawHandoffStartedRef.current)) {
          break;
        }
        // Unusual ordering (this frame arrived before its HANDOFF_STARTED
        // pair): still nothing widget-related left to do, but the turn IS
        // over, so clear the working indicator same as before.
        ctx.setTurnState(null);
        break;
      }

      case WSEventType.CONTACT_INFO_REQUESTED: {
        // Decision #17 (pre-handoff contact form): the handoff row is in
        // `pending_contact` — no HubSpot thread exists yet, so uiState
        // deliberately STAYS CHAT (not HANDOFF) here. Sara/the input bar
        // remain fully live while the inline contact-info-card.tsx renders
        // (see page.tsx) — this is the whole point of the "form gates the
        // HubSpot connection, Sara stays active" design. No local notice
        // text: the backend owns all handoff notice copy (2026-07-06 field-
        // testing fix), and the card itself IS the UI signal here — there's
        // no separate "please fill this in" system message to send.
        //
        // Also does NOT touch turnState: unlike HANDOFF_STARTED, the
        // assistant's turn isn't necessarily over (the tool call that
        // triggered this may still be the live turn), so there's nothing to
        // clear here that TOOL_END/AGENT_END/turn_state:idle wouldn't
        // already handle through their normal paths.
        //
        // Mid-voice-call: the form card lives in the chat conversation area,
        // which isn't mounted during a call — and Decision #3 says a handoff
        // is text-only. Before form gating, HANDOFF_STARTED did this
        // call-stop; now this event arrives first, so it owns the switch.
        // uiState goes to CHAT (not HANDOFF — that still belongs to
        // HANDOFF_STARTED, after the form is submitted).
        //
        // [FB34] `stopCallIfActive()` (phase-aware) replaces the old
        // `isCalling`-based guard + direct `stopCall()` — isCalling misses
        // `connecting`/`failed` — and its return value doubles as `wasCalling`
        // for shouldDeferContactForm below.
        const wasCalling = ctx.stopCallIfActive();
        if (wasCalling) {
          ctx.setUIState(UIState.CHAT);
        }
        const handoffId = event.handoff_id as string;
        const fields = event.fields as ContactField[];
        // This event fires at TOOL time — before Sara's "please fill this in"
        // prose exists — so mounting the card now makes it pop in first and
        // then get pushed down when the text lands (the tool cards' original
        // "card above prose" bug, temporally). Defer it through the same
        // stash/flush cycle so it mounts right after the prose; insert
        // immediately in voice mode / on a reconnect re-send (no prose is
        // coming — see shouldDeferContactForm).
        if (shouldDeferContactForm(wasCalling, turnActiveRef.current)) {
          deferredContactFormRef.current = { handoffId, fields };
          armDeferredFlushFallback();
        } else {
          ctx.contactInfoRequested(handoffId, fields);
        }
        break;
      }

      case WSEventType.CONTACT_SUMMARY: {
        // Decision #18.1: post-submit "Details shared" receipt. Fires once,
        // deterministically BEFORE HANDOFF_STARTED (see ContactSummaryEvent's
        // docstring in aol_schemas.ws_events) — inserted as a transcript
        // item (not a banner), so it lands exactly where the now-submitted
        // contact form card sat, right before the banner switches to its
        // "Connecting you to live support…" copy (handoff-banner-text.ts).
        // No uiState/turnState change here — the tool call that led here may
        // still be the live turn; HANDOFF_STARTED (next) owns that.
        ctx.insertContactSummary({
          handoff_id: event.handoff_id as string,
          name: event.name as string,
          email: event.email as string,
          phone: event.phone as string | null | undefined,
        });
        break;
      }

      case WSEventType.HANDOFF_STARTED: {
        // The turn resolves into a human handoff — no further assistant
        // output is coming; drop any working indicator.
        ctx.setTurnState(null);
        sawHandoffStartedRef.current = true;
        const handoffId = event.handoff_id as string;
        const domain = event.domain as HandoffDomain;

        // Decision #3: a voice call always ends cleanly and switches to text
        // before a handoff proceeds (same call-stopping logic the legacy
        // HANDOFF_TRIGGER handler used — minus the widget).
        //
        // [FB34] `stopCallIfActive()` (phase-aware) — isCalling/isCallingRef
        // alone miss `connecting`/`failed`.
        ctx.stopCallIfActive();

        ctx.startHandoff(handoffId, domain);
        ctx.setUIState(UIState.HANDOFF);
        // NOTE: no local "Connecting you to live support…" notice here anymore.
        // The backend is now the single source of truth for handoff notice
        // copy (2026-07-06 field-testing fix) — it sends that copy as a
        // SYSTEM_MESSAGE frame (handled below), and the live status while
        // waiting/connected is shown by HandoffBanner (see
        // src/lib/handoff-banner-text.ts), not a transcript chip. This case
        // now only performs the phase/UI-state transition.
        break;
      }

      case WSEventType.AGENT_JOINED: {
        const agentName = event.agent_display_name as string;
        ctx.agentJoined(agentName);
        // NOTE: no local "{name} joined" notice — the backend sends that copy
        // via SYSTEM_MESSAGE, and the banner's phase text (HandoffPhase.CONNECTED)
        // already reflects the joined agent's name.
        break;
      }

      case WSEventType.AGENT_MESSAGE: {
        const agentName = event.agent_display_name as string;
        // A message landing here is proof a human is live even if
        // AGENT_JOINED's webhook signal was delayed/missed — auto-promotes
        // 'waiting' → 'connected' (no-op otherwise). See useHandoff.
        ctx.agentMessageReceived(agentName);
        ctx.insertHumanAgentMessage({
          message_id: event.message_id as string,
          text: event.text as string,
          rich_text: event.rich_text as string | null | undefined,
          agent_display_name: agentName,
          attachments: event.attachments as AgentAttachment[] | undefined,
        });
        setTimeout(() => ctx.chatLayoutRef.current?.scrollToBottom(300), 10);
        break;
      }

      case WSEventType.HANDOFF_ENDED: {
        // NOTE: no local end-of-handoff notice ("The support conversation has
        // ended.") — the backend sends that as a SYSTEM_MESSAGE frame
        // (handled below), including the "Your chat with {name} has ended…"
        // copy. This case only performs the state transition: the banner
        // (keyed off handoff.phase) disappears and the UI returns to normal
        // chat. `end_reason` is threaded through (not dropped) so recovery UI
        // can distinguish agent_resolved from user_ended — see
        // HandoffContext.endReason.
        ctx.markHandoffEnded(event.end_reason as HandoffEndReason);
        ctx.setUIState(UIState.CHAT);
        break;
      }

      case WSEventType.HANDOFF_TIMEOUT: {
        // No rep ever joined before the backend's no-agent timeout — its own
        // phase (TIMED_OUT), not ENDED, so presence-ribbon.tsx can offer
        // timeout-specific recovery copy instead of the generic "ended"
        // silence. Same "no local notice, backend sends SYSTEM_MESSAGE" note
        // as HANDOFF_ENDED above.
        ctx.markHandoffTimedOut();
        ctx.setUIState(UIState.CHAT);
        break;
      }

      case WSEventType.ERROR: {
        // Bucket-aware after the 2026-05 refactor — see types/chat.ts.
        // The user-facing system message arrives via SYSTEM_MESSAGE; this
        // handler's job is to clear in-flight UI state and (for FATAL)
        // surface a second-channel toast so the user can't miss it.
        // (Casts are required because handleServerEvent's `event` parameter is
        // Record<string, unknown> from the JSON.parse boundary; the typed
        // WSServerMessage union narrows at compile time but not here.)
        const bucket = event.bucket as ErrorBucket;
        const userMessage = event.user_message as string;
        console.error('Server error:', bucket, event.code, userMessage);
        if (ctx.isThinkingRef.current) {
          ctx.isThinkingRef.current = false;
          ctx.stopThinkingTicker();
          ctx.resetToIdle();
        }
        if (event.code === RealtimeErrorCode.HISTORY_REPLAY_FAILED) {
          // Restore failed but the session is healthy (bucket=BENIGN). Flip the
          // store flag so page.tsx releases any queued ?prompt= immediately
          // (instead of waiting out the fallback timeout), and surface the note.
          useConversationStore.getState().setHistoryReplayFailed(true);
          // [machine] The awaited replay is dead — stop showing the skeleton.
          useConversationStore.getState().setExpectingHistory(false);
          toast.error(userMessage);
        } else if (bucket === ErrorBucket.FATAL) {
          // Cheap second channel for the worst case — if the SYSTEM_MESSAGE
          // render path is broken for any reason, the user still sees this.
          toast.error(userMessage);
        }
        if (bucket !== ErrorBucket.BENIGN) {
          // [machine] Non-benign error kills the in-flight turn. The backend
          // emits turn_state:idle before this event; clearing locally too is
          // a belt-and-suspenders against a dropped/reordered idle frame.
          ctx.setTurnState(null);
        }
        break;
      }

      case WSEventType.SYSTEM_MESSAGE: {
        // Backend-originated in-character message (e.g., reconnect notice).
        // Append to the conversation as a system-role Message so it persists
        // alongside user/assistant turns in the chat history.
        const content = event.content as string | undefined;
        if (!content) break;
        const sysMsg: Message = {
          role: MessageRole.SYSTEM,
          content,
          id: `sys-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        };
        ctx.setMessages((prev) => [...prev, sysMsg]);
        break;
      }

      case WSEventType.RATE_LIMIT_WAIT: {
        // [FB32] OpenAI rate-limited this response; the backend already
        // sent (or will send) its normal turn_state:"thinking" frame
        // alongside this one, so the shimmer/"Working…" timeline stays
        // alive on its own. In a call, ctx.onModelBusy drives the Turn-15
        // patience/exhausted notice.
        const retryIn = (event.retry_in as number | null | undefined) ?? null;
        ctx.onModelBusy(retryIn);
        // onModelBusy no-ops outside a call (no call surface to host a
        // CallNotice). For the WAITING case (retry_in set) that's fine —
        // the "Working…" timeline covers it. But the EXHAUSTED case
        // (retry_in null) means the backend has GIVEN UP: the timeline just
        // stops with no explanation and no retry affordance (the "Ask again"
        // chip is call-only). Surface a toast so the text user knows to try
        // again, rather than staring at a turn that will never answer.
        if (retryIn === null && !ctx.isCallingRef.current) {
          toast.error(
            "Sara's having trouble responding right now. Please try asking again."
          );
        }
        break;
      }

      case WSEventType.RECONNECTING: {
        // Transparent UPSTREAM reconnect (backend ↔ OpenAI) in progress — our
        // socket is fine, so this is a display-only overlay on the badge; the
        // machine deliberately does not model upstream health (D6). No message
        // appears in chat unless reconnect ultimately fails (FATAL).
        ctx.setConnectionStatus(ConnectionStatus.RECONNECTING);
        // Settle any in-flight stream so a partial caret isn't stranded across
        // the reconnect; the continued/restored history reconciles the full turn.
        ctx.finalizeStreaming();
        // Only clear the thinking ticker if it's actually running — avoids
        // a redundant viz-mode reset on every reconnect attempt.
        if (ctx.isThinkingRef.current) {
          ctx.isThinkingRef.current = false;
          ctx.stopThinkingTicker();
          ctx.resetToIdle();
        }
        break;
      }

      case WSEventType.RECONNECTED: {
        ctx.setConnectionStatus(ConnectionStatus.CONNECTED);
        break;
      }

      // [SDP-over-WS] Pure transport frames for the WebRTC call-start/hangup
      // negotiation carried over this socket (see rtc-ws-negotiate.ts) —
      // consumed directly by that module's pending-request registry, never
      // touching ChatContext or the reducer. No case needed for the
      // client → server rtc_call/rtc_hangup messages themselves; those are
      // sent directly on the socket by rtc-call.ts/useCallMode.ts, mirroring
      // the END_HANDOFF/CONTACT_INFO one-shot-control-frame precedent.
      case WSEventType.RTC_CALL_ACK:
      case WSEventType.RTC_CALL_ANSWER:
      case WSEventType.RTC_CALL_FAILED: {
        handleRtcWsEvent(
          event as {
            type: string;
            request_id?: string;
            sdp?: string;
            call_id?: string;
            reason?: string;
          }
        );
        break;
      }

      // Page-context handshake ack (page-context-plan.md §4) — consumed
      // directly by the page-context decision machine, never touching
      // ChatContext or the outbox reducer. See dispatchPageContext above.
      case WSEventType.PAGE_CONTEXT_ACK: {
        const requestId = event.request_id as string | undefined;
        const status = event.status as PageContextAckStatus | undefined;
        if (!requestId || !status) {
          console.warn(
            '[page-context] malformed page_context_ack — missing request_id/status',
            event
          );
          break;
        }
        dispatchPageContext({
          type: 'ACK_RECEIVED',
          requestId,
          status,
          missing: event.missing as string[] | undefined,
          reason: event.reason as string | null | undefined,
        });
        break;
      }

      case WSEventType.MESSAGE_RECEIVED: {
        // [machine] Delivery ack: the backend received (and will process) the
        // text frame carrying this client_msg_id. Resolves the outbox item →
        // the bubble's "Sending…" clears. Also the capability proof that this
        // backend speaks the ack protocol (enables timeout-fail + safe
        // auto-resend — see reducer.ts).
        const clientMsgId = event.client_msg_id as string | undefined;
        if (clientMsgId) {
          dispatch({ type: 'ACK_RECEIVED', id: clientMsgId });
        }
        break;
      }

      case WSEventType.MESSAGE_PERSISTED: {
        // [id bridge] The assistant turn's text row is now durably written —
        // item_id is the client-visible id the bubble is ALREADY keyed by
        // (a Realtime item id, or insertAssistantMessage's assistant-<hash>
        // fallback); message_id is the DB-minted UUID feedback.message_id
        // actually requires. Delegate the lookup-and-stamp to
        // useMessageManagement (that hook owns message-list mutation; this
        // driver only dispatches) — a no-match is a safe no-op there, logged
        // rather than thrown, so a stale/out-of-order frame can never crash
        // the session over a cosmetic feedback affordance. See
        // MessagePersistedEvent's docstring (ws-dto.d.ts) for the full "why".
        const itemId = event.item_id as string | undefined;
        const dbId = event.message_id as string | undefined;
        if (itemId && dbId) ctx.setMessageDbId(itemId, dbId);
        break;
      }

      case WSEventType.TOOL_TIMEOUT_OFFER: {
        // [session-recovery] An MCP tool call (find_courses /
        // browse_aol_content) timed out and a retry is being offered —
        // ToolTimeoutCard (mounted at the transcript tail by page.tsx) reads
        // this off ctx.toolTimeoutOffer. Wholesale replace, not a merge —
        // this is the SAME "always a full replace" precedent as
        // buildCollectingHandoff (useHandoff.ts): a repeated event for the
        // same or a different tool always describes the CURRENT truth.
        // `retry_id` (required, no optional — see ToolTimeoutOfferState's
        // doc comment) is what useToolTimeoutFlow now uses to detect a
        // fresh incident and correlate the eventual outcome — the bumped
        // `attempt` alone is no longer what drives that. `tool_label` is
        // deliberately NOT read (copy review — see ToolTimeoutOfferState's
        // own doc comment): still present on the wire for now (the backend
        // is removing it in parallel), but nothing here renders it any
        // more, so parsing it would just be dead code.
        const toolName = event.tool_name as string | undefined;
        const attempt = event.attempt as number | undefined;
        const retryAvailable = event.retry_available as boolean | undefined;
        const retryId = event.retry_id as string | undefined;
        if (
          toolName &&
          typeof attempt === 'number' &&
          typeof retryAvailable === 'boolean' &&
          retryId
        ) {
          ctx.setToolTimeoutOffer({
            toolName,
            attempt,
            retryAvailable,
            retryId,
          });
        } else {
          // Loud, not silent (session-recovery hazard review): a required
          // field going missing/malformed here is EXACTLY the failure
          // shape that let a stale gate silently kill this whole feature
          // with green tests when tool_label/tool_name were removed from
          // the wire — see this case's own comment above. A malformed
          // frame is rare and worth knowing about, not a routine no-op.
          console.warn(
            '[TOOL_TIMEOUT_OFFER] Dropping malformed frame — missing/invalid required field(s)',
            event
          );
        }
        break;
      }

      case WSEventType.TOOL_RETRY_OUTCOME: {
        // [session-recovery] The explicit success/failure/abandonment signal
        // for ONE retry_tool_call attempt — REPLACES the old turnState
        // busy-then-idle inference (unsound: turnState going idle for ANY
        // reason — a dropped socket, an unrelated turn finishing, a
        // barge-in — used to read as a false success). LIVE-ONLY, never
        // persisted; wholesale replace like TOOL_TIMEOUT_OFFER above.
        // useToolTimeoutFlow (composed once in ChatContext) correlates this
        // against the CURRENT incident's retryId before acting on it — a
        // mismatched id (a stale outcome from a superseded incident) is
        // ignored there, not here. `tool_name` is deliberately NOT read
        // (removed from the wire entirely — see ToolRetryOutcomeState's own
        // doc comment): it was captured into state and never actually read
        // by anything, the same dead-field hazard `toolLabel`/`call_id`
        // already were.
        const retryId = event.retry_id as string | undefined;
        const result = event.result as
          | ToolRetryOutcomeState['result']
          | undefined;
        if (retryId && result) {
          ctx.setToolRetryOutcome({ retryId, result });
        } else {
          // Loud, not silent — same reasoning as TOOL_TIMEOUT_OFFER above.
          console.warn(
            '[TOOL_RETRY_OUTCOME] Dropping malformed frame — missing/invalid required field(s)',
            event
          );
        }
        break;
      }

      case WSEventType.HISTORY_RESTORED: {
        const restoredMessages = event.messages as RestoredRow[] | undefined;

        if (restoredMessages && Array.isArray(restoredMessages)) {
          // The backend sends the MOST-RECENT page ordered by seq (display order,
          // text-before-card) via the shared mapper, plus a cursor for scroll-up.
          const parsed = restoredMessagesToMessages(restoredMessages);
          ctx.setMessages(parsed);
          // Mid-call reconnect (FB28): the restore just replaced the whole
          // message array, so the transcript rail's call-start boundary — a
          // plain index captured at call start — no longer points where it
          // did. Re-derive it from row timestamps so the rail keeps showing
          // exactly this call's turns.
          if (ctx.isCallingRef.current) {
            ctx.callStartMessageIndexRef.current = callStartIndexFromRestore(
              parsed,
              ctx.callStartedAtRef.current
            );
          }
          // [machine] Re-append in-flight outbox bubbles the snapshot can't
          // know about: their frames were sent AFTER the settings frame, so
          // the restore page predates them. Without this, a
          // reconnect-with-restore (e.g. a Retry reviving a failed
          // connection) clobbers the optimistic bubble of a message that is
          // still being delivered — the reply then arrives with no question
          // above it. They render with their live status and resolve on ack.
          const inflight = machineRef.current.outbox;
          if (inflight.length > 0) {
            ctx.setMessages((prev) => {
              const ids = new Set(prev.map((m) => m.id));
              const missing = inflight.filter((i) => !ids.has(i.id));
              if (missing.length === 0) return prev;
              return [
                ...prev,
                ...missing.map(
                  (i): Message => ({
                    role: MessageRole.USER,
                    content: i.text,
                    id: i.id,
                    status: i.status,
                  })
                ),
              ];
            });
          }
          ctx.setUIState(UIState.CHAT);
          // Restore the conversation title so the header shows the real title
          // instead of "New Conversation". The ?c= / reload restore path never
          // set activeConversationTitle before (initFromUrl carries only the id,
          // and TITLE_UPDATED fires only on generation, not replay). Guarded on a
          // non-empty string so a null title keeps the client's fallback.
          const restoredTitle = event.title;
          if (typeof restoredTitle === 'string' && restoredTitle) {
            useConversationStore
              .getState()
              .setActiveConversationTitle(restoredTitle);
          }
          // [machine] The awaited replay landed — the resume skeleton is done.
          useConversationStore.getState().setExpectingHistory(false);
          // Seed the scroll-up pagination cursor.
          const nextBeforeSeq = event.next_before_seq;
          useConversationStore
            .getState()
            .setHistoryPage(
              typeof nextBeforeSeq === 'number' ? nextBeforeSeq : null,
              event.has_more === true
            );
          setTimeout(() => ctx.chatLayoutRef.current?.scrollToBottom(300), 100);
        }
        break;
      }

      case WSEventType.CONVERSATION_RESET: {
        // The ?c= we sent wasn't loadable for THIS device (foreign / not-found).
        // The backend started a fresh conversation and sent its id. Adopt it —
        // page.tsx's URL-sync effect then swaps ?c= to it — without reconnecting
        // (the WS is already live on this new conversation), and tell the user so
        // the unexpected "empty thread" isn't silent.
        const newId = event.conversation_id as string | undefined;
        if (newId) {
          // resetConversation() adopts the new id AND sets the resetNotice flag,
          // which drives a declarative inline banner (ResetNotice). We deliberately
          // do NOT toast here: the id change triggers a router.replace, and a
          // one-shot toast dispatched during that navigation is dropped by the
          // Next-router × Sonner interaction; a state-driven banner is reliable.
          useConversationStore.getState().resetConversation(newId);
        }
        // [machine] Fresh conversation — no turn can be in flight on it.
        ctx.setTurnState(null);
        break;
      }

      case WSEventType.AUDIO_INTERRUPTED: {
        ctx.clearPlayback();
        if (ctx.isThinkingRef.current) {
          ctx.isThinkingRef.current = false;
          ctx.stopThinkingTicker();
          ctx.resetToIdle();
        }
        // The in-flight utterance was cut short (user barge-in). Settle its
        // caret now rather than leaving it stranded mid-word — the content
        // itself stays (already durable in `messages` via
        // appendAssistantDelta); the post-interrupt authoritative snapshot
        // (status 'incomplete' or 'completed') reconciles it moments later
        // via the HISTORY_UPDATED authority rule
        // (resolveHistoryItemReconcile, lib/history-reconcile.ts).
        if (ctx.isCallingRef.current) ctx.finalizeStreaming();
        break;
      }

      case WSEventType.TITLE_UPDATED: {
        const title = event.title as string | undefined;
        if (title) {
          // generated === false marks the truncated-first-message
          // placeholder (backend sets it the moment the first user message
          // persists, purely so the SIDEBAR row isn't blank while the LLM
          // title cooks). The header skips it: echoing the user's own
          // question — already visible in the transcript — made the title
          // flick New Conversation → question → LLM title within seconds.
          // `!== false` (not `=== true`) keeps an older backend without
          // the field behaving exactly as before this change.
          const generated = (event as { generated?: boolean }).generated;
          if (generated !== false) {
            useConversationStore.getState().setActiveConversationTitle(title);
          }
          // Refresh the conversation list so the sidebar shows
          // the new/updated title without reopening the popover.
          queryClient.invalidateQueries({ queryKey: ['conversations'] });
        }
        break;
      }

      default:
        break;
    }
  }
}
